/**
 * MultiLLMComparisonPage - Multi-LLM output comparison dashboard
 *
 * Surfaces the MultiLLMComparisonDashboard from @ferp/workflow-forms for admin/architect users.
 * Enables comparison of outputs across multiple LLM providers for quality assurance.
 *
 * Route: /admin/llm-comparison
 */

import { Container, Title, Group, Badge, Text, ThemeIcon, Alert } from '@mantine/core';
import { IconBrain, IconInfoCircle } from '@tabler/icons-react';
import { MultiLLMComparisonDashboard } from '@ferp/workflow-forms';
import { useAuth } from '@/auth/AuthContext';
import { useSearchParams } from 'react-router-dom';

// Demo ensemble result for visualization
const demoEnsembleResult = {
  llmResults: [
    {
      llmName: 'Claude 3.5 Sonnet',
      llmVersion: '20240620',
      confidence: 0.92,
      extractedFields: {
        patient_name: 'John Doe',
        date_of_birth: '1985-03-15',
        diagnosis: 'Hypertension',
      },
      processingTime: 1250,
      tokensUsed: 450,
    },
    {
      llmName: 'GPT-4',
      llmVersion: 'gpt-4-turbo',
      confidence: 0.88,
      extractedFields: {
        patient_name: 'John Doe',
        date_of_birth: '1985-03-15',
        diagnosis: 'Essential Hypertension',
      },
      processingTime: 1800,
      tokensUsed: 520,
    },
    {
      llmName: 'Gemini Pro',
      confidence: 0.85,
      extractedFields: {
        patient_name: 'John Doe',
        date_of_birth: '1985-03-15',
        diagnosis: 'Hypertension',
      },
      processingTime: 1100,
      tokensUsed: 380,
    },
  ],
  consensusConfidence: 0.88,
  disagreementFields: ['diagnosis'],
  totalFields: 3,
  consensusFields: 2,
};

export function MultiLLMComparisonPage() {
  const { tenant } = useAuth();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="grape">
            <IconBrain size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>LLM Comparison</Title>
            <Text size="sm" c="dimmed">
              Compare outputs across multiple LLM providers for quality assurance
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      {!templateId && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" mb="md">
          Showing demo data. In production, navigate here from the extraction review
          workflow to see actual multi-LLM comparison results.
        </Alert>
      )}

      <MultiLLMComparisonDashboard
        templateId={templateId ?? 'demo-template'}
        ensembleResult={demoEnsembleResult}
        onLLMClick={(llmName) => console.log('LLM clicked:', llmName)}
      />
    </Container>
  );
}

export default MultiLLMComparisonPage;
